import type { ApiNodeDataComplete } from '@onefootprint/request-types/dashboard';
import { useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import useDirty from '@/components/playbook-builder/hooks/use-dirty';
import { useGetNodeConfigContext } from '@/components/playbook-builder/hooks/use-get-node-config-context';
import type { NodeProps } from '@/playbook-builder/playbook-builder.types';
import type { ApplyEditMutation } from '../../../base-node';
import { BifrostFrame, NodePreviewLayout } from '../../../shared/node-preview';
import { BLANK_CTA, BLANK_SECONDARY_CTA, COMPLETE_FORM_ID } from '../../complete-node.constants';
import type { CompleteFormData, PreviewSelection } from '../../complete-node.types';
import { toAutocompleteItems } from '../../to-autocomplete-items';
import { CompleteNodeFields } from '../complete-node-fields';
import { CompleteNodePreview } from '../complete-node-preview/complete-node-preview';
import { usePanelState } from './use-panel-state';
type CompleteNodeFormProps = {
  node: NodeProps<ApiNodeDataComplete>;
  mutation: ApplyEditMutation;
  canEdit: boolean;
  /** What the preview starts with picked out, for a still frame of a selection. */
  initialSelection?: PreviewSelection;
};
export const CompleteNodeForm = ({
  node,
  mutation,
  canEdit,
  initialSelection
}: CompleteNodeFormProps) => {
  const {
    toggle
  } = useDirty();
  const {
    page
  } = node.data;
  const form = useForm<CompleteFormData>({
    disabled: !canEdit,
    defaultValues: {
      header: page.header || '',
      subheader: page.subheader || '',
      content: page.content || '',
      // Read off the second: a page carrying only a second CTA is one the backend rejects, and
      // counting both would put that CTA's text in the first slot and save it as a first.
      ctaCount: page.secondaryCta ? 2 : page.cta ? 1 : 0,
      ctaLabel: page.cta?.label || BLANK_CTA.ctaLabel,
      ctaUrl: page.cta?.url || BLANK_CTA.ctaUrl,
      ctaTarget: page.cta?.target || BLANK_CTA.ctaTarget,
      secondaryCtaLabel: page.secondaryCta?.label || BLANK_SECONDARY_CTA.secondaryCtaLabel,
      secondaryCtaUrl: page.secondaryCta?.url || BLANK_SECONDARY_CTA.secondaryCtaUrl,
      secondaryCtaTarget: page.secondaryCta?.target || BLANK_SECONDARY_CTA.secondaryCtaTarget,
      secondaryCtaStyle: page.secondaryCta?.style || BLANK_SECONDARY_CTA.secondaryCtaStyle,
      autoAdvance: page.autoAdvanceAfterSecs != null,
      autoAdvanceSecs: page.autoAdvanceAfterSecs ?? null
    }
  });
  const panel = usePanelState(form, initialSelection);
  const ctaCount = useWatch({
    control: form.control,
    name: 'ctaCount'
  });
  const config = useGetNodeConfigContext(node.id, 'templatable');
  const autocompleteItems = toAutocompleteItems(config?.autocomplete);
  useEffect(() => {
    toggle(COMPLETE_FORM_ID, form.formState.isDirty);
  }, [form.formState.isDirty]);
  const handleFormSubmit = async (data: CompleteFormData) => {
    try {
      await mutation.mutateAsync({
        kind: 'update_node',
        nodeId: node.id,
        nodeData: {
          kind: 'complete',
          page: {
            header: data.header,
            subheader: data.subheader || undefined,
            content: data.content || undefined,
            cta: data.ctaCount > 0 ? {
              label: data.ctaLabel,
              url: data.ctaUrl,
              target: data.ctaTarget
            } : undefined,
            secondaryCta: data.ctaCount > 1 ? {
              label: data.secondaryCtaLabel,
              url: data.secondaryCtaUrl,
              target: data.secondaryCtaTarget,
              style: data.secondaryCtaStyle
            } : undefined,
            autoAdvanceAfterSecs: data.autoAdvance && data.autoAdvanceSecs != null ? data.autoAdvanceSecs : undefined
          }
        }
      });
    } catch (_) {}
  };
  return <FormProvider {...form}>
      <NodePreviewLayout preview={<BifrostFrame interactive={canEdit}>
            <CompleteNodePreview selection={canEdit ? {
        selected: panel.selected,
        onSelect: panel.selectFromPreview,
        // The backend rejects a second CTA with no first, so the first stays while one follows.
        onRemove: slot => slot === 'primary' && ctaCount > 1 ? undefined : () => panel.removeCta(slot)
      } : undefined} />
          </BifrostFrame>}>
        <form id={COMPLETE_FORM_ID} onSubmit={form.handleSubmit(handleFormSubmit, panel.openAllSections)} className="flex flex-col gap-4">
          <CompleteNodeFields autocompleteItems={autocompleteItems} canEdit={canEdit} panel={panel} />
        </form>
      </NodePreviewLayout>
    </FormProvider>;
};